import { useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { QUOTES, AUTHORS, pick, shuffle, quotesByPack, type PackId } from "@/data/quotes";
import { ANSWER_LETTERS, ROUND_COUNT, loadBest, saveBest, loadStreak, saveStreak } from "@/lib/game-utils";

interface Round { quoteId: string; options: string[]; correct: string; }

function buildRounds(pack: PackId): Round[] {
  const pool = quotesByPack(pack);
  const source = pool.length >= ROUND_COUNT ? pool : QUOTES;
  return pick(source, ROUND_COUNT).map((q) => {
    const distractors = shuffle(AUTHORS.filter((a) => a !== q.author)).slice(0, 3);
    return { quoteId: q.id, options: shuffle([q.author, ...distractors]), correct: q.author };
  });
}

export function WhoSaidThis({ pack = "all" }: { pack?: PackId }) {
  const live = useLiveMode();
  const [rounds, setRounds] = useState<Round[]>(() => buildRounds(pack));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [best, setBest] = useState(0);
  useEffect(() => {
    setBest(loadBest("who-said-this"));
    setBestStreak(loadStreak("who-said-this"));
  }, []);

  const round = rounds[idx];
  const quote = useMemo(() => QUOTES.find((q) => q.id === round?.quoteId)!, [round]);

  const answer = (opt: string) => {
    if (picked) return;
    setPicked(opt);
    if (opt === round.correct) {
      setScore((s) => s + 1);
      setStreak((s) => {
        const next = s + 1;
        if (next > bestStreak) setBestStreak(saveStreak("who-said-this", next));
        return next;
      });
    } else {
      setStreak(0);
    }
  };
  const next = () => {
    if (idx + 1 >= rounds.length) {
      setBest(saveBest("who-said-this", score));
      setDone(true);
    } else {
      setIdx(idx + 1);
      setPicked(null);
    }
  };
  const restart = () => {
    setRounds(buildRounds(pack));
    setIdx(0); setScore(0); setStreak(0); setPicked(null); setDone(false);
  };

  if (done) return <FinishScreen score={score} total={rounds.length} best={best} bestStreak={bestStreak} onRestart={restart} title="Who Said This?" numeral="I" hubSlug="who-said-this" />;

  return (
    <GameShell title="Who Said This?" numeral="I" score={score} round={idx + 1} total={rounds.length} streak={streak} timeLimit={20} timeKey={idx} onTimeUp={() => !picked && setPicked("__timeout__")} hubSlug="who-said-this">
      <article className="rounded-2xl border border-border bg-card p-8 sm:p-12 shadow-elegant grain animate-reveal">
        <div className="quote-mark" />
        <p className={`font-display leading-tight ${live ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{quote.text}</p>
      </article>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {round.options.map((opt, i) => {
          const correct = picked && opt === round.correct;
          const wrong = picked === opt && opt !== round.correct;
          return (
            <button key={opt} onClick={() => answer(opt)} disabled={!!picked}
              className={`group flex items-center gap-4 rounded-xl border p-4 text-left transition ${
                correct ? "border-success bg-success/10" : wrong ? "border-error bg-error/10 animate-shake" : picked ? "border-border opacity-50" : "border-border hover:border-gold hover:bg-accent"
              }`}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-mono font-bold ${correct ? "bg-success text-ink" : wrong ? "bg-error text-cream" : "bg-secondary text-gold"}`}>{ANSWER_LETTERS[i]}</span>
              <span className={`font-medium ${live ? "text-xl" : ""}`}>{opt}</span>
            </button>
          );
        })}
      </div>

      {picked && (
        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm">
              {picked === round.correct
                ? <span className="text-success font-medium">✓ Correct!</span>
                : <span className="text-error font-medium">✗ The author was <strong className="text-gold">{round.correct}</strong></span>}
            </div>
            <button onClick={next} className="rounded-full bg-gradient-gold px-5 py-2 text-sm font-semibold text-ink">{idx + 1 >= rounds.length ? "See results" : "Next →"}</button>
          </div>
          {(quote.source || quote.era) && (
            <div className="mt-2 text-xs text-muted-foreground">
              {quote.author}{quote.era ? ` · ${quote.era}` : ""}{quote.source ? ` · ${quote.source}` : ""}
            </div>
          )}
        </div>
      )}
    </GameShell>
  );
}
